import React, 
       { useState }                 from 'react'
import { Box,
         TextField,
         FormControl,
         Select,
         MenuItem,
         IconButton,
         Typography,
         Button,
         useTheme,
       }                            from '@mui/material'
import AddIcon                      from '@mui/icons-material/Add'
import { SubChoice }                from 'types/apis/surveyInterfaces'
import { Trash2Icon }               from 'lucide-react'

export interface SubChoicesSectionProps {
  subChoices: SubChoice[];
  onSubChoicesChange: (subChoices: SubChoice[]) => void;
  isReadOnly?: boolean;
  parentChoiceIndex: number;
  isSubChoiceFieldModified?: (choiceIndex: number, subChoiceIndex: number, fieldName: keyof SubChoice) => boolean;
  getModifiedChoiceFieldStyle?: (isModified: boolean) => any;
  getModifiedChoiceSelectStyle?: (isModified: boolean) => any;
}

export const SubChoicesSection: React.FC<SubChoicesSectionProps> = ({
  subChoices,
  onSubChoicesChange,
  isReadOnly = false,
  parentChoiceIndex,
  isSubChoiceFieldModified,
  getModifiedChoiceFieldStyle,
  getModifiedChoiceSelectStyle,
}) => {
  const [jsonErrors, setJsonErrors] = useState<Record<number, string>>({});
  const theme = useTheme();

  // Common label component for form fields
  const renderFormLabel = (label: string, required: boolean = false) => (
    <Typography 
      variant="body2" 
      sx={{ 
        color: theme.palette.text.secondary,
        fontWeight: 500,
      }}
    >
      {label} {required && <span style={{ fontSize: '0.75rem', color: theme.palette.error.main }}>*</span>}
    </Typography>
  )

  const handleAddSubChoice = () => {
    const newSubChoice: SubChoice = {
      title: '',
      value: '',
      order: subChoices.length + 1,
      selectionType: 'NONE',
      subChoiceValidationType: 'NONE',
    };
    onSubChoicesChange([...subChoices, newSubChoice]);
  };

  const handleDeleteSubChoice = (index: number) => {
    const updated = subChoices.filter((_, i) => i !== index);
    // Reorder remaining sub-choices
    const reordered = updated.map((sc, i) => ({ ...sc, order: i + 1 }));
    onSubChoicesChange(reordered);
  };

  const handleSubChoiceChange = (index: number, field: keyof SubChoice, value: any) => {
    const updated = [...subChoices];
    updated[index] = { ...updated[index], [field]: value };
    onSubChoicesChange(updated);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
        Sub Choices
      </Typography>
      {subChoices.map((subChoice, index) => (
        <Box
          key={index}
          sx={{
            backgroundColor: theme.palette.background.default,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            mb: 2,
            position: 'relative',
          }}
        >
          {subChoices.length > 0 && !isReadOnly && (
            <IconButton
              size="small"
              onClick={() => handleDeleteSubChoice(index)}
              sx={{
                position: 'absolute',
                right: 2,
                top: 2,
                color: 'error.main',
              }}
            >
              <Trash2Icon size={20} />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <>
              {renderFormLabel('Title', true)}
              <TextField
                value={subChoice.title}
                onChange={(e) => handleSubChoiceChange(index, 'title', e.target.value)}
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: theme.palette.background.paper,
                  },
                  ...(getModifiedChoiceFieldStyle && isSubChoiceFieldModified
                    ? getModifiedChoiceFieldStyle(isSubChoiceFieldModified(parentChoiceIndex, index, 'title'))
                    : {}),
                }}
                required
                disabled={isReadOnly}
              />
            </>
            <>
              {renderFormLabel('Value', true)}
              <TextField
                value={subChoice.value}
                onChange={(e) => handleSubChoiceChange(index, 'value', e.target.value)}
                fullWidth
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: theme.palette.background.paper,
                  },
                  ...(getModifiedChoiceFieldStyle && isSubChoiceFieldModified
                    ? getModifiedChoiceFieldStyle(isSubChoiceFieldModified(parentChoiceIndex, index, 'value'))
                    : {}),
                }}
                size="small"
                required
                disabled={isReadOnly}
              />
            </>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth size="small">
                {renderFormLabel('Selection Type')}
                <Select
                  value={subChoice.selectionType}
                  onChange={(e) => handleSubChoiceChange(index, 'selectionType', e.target.value)}
                  disabled={isReadOnly}
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: theme.palette.background.paper,
                    },
                    ...(getModifiedChoiceSelectStyle && isSubChoiceFieldModified
                      ? getModifiedChoiceSelectStyle(isSubChoiceFieldModified(parentChoiceIndex, index, 'selectionType'))
                      : {}),
                  }}
                >
                  <MenuItem value="NONE">NONE</MenuItem>
                  <MenuItem value="LIST">LIST</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                {renderFormLabel('Validation Type')}
                <Select
                  value={subChoice.subChoiceValidationType}
                  onChange={(e) => handleSubChoiceChange(index, 'subChoiceValidationType', e.target.value)}
                  disabled={isReadOnly}
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: theme.palette.background.paper,
                    },
                    ...(getModifiedChoiceSelectStyle && isSubChoiceFieldModified
                      ? getModifiedChoiceSelectStyle(isSubChoiceFieldModified(parentChoiceIndex, index, 'subChoiceValidationType'))
                      : {}),
                  }}
                >
                  <MenuItem value="NONE">NONE</MenuItem>
                  <MenuItem value="ASC_ALLOWED_VALUES">ASC_ALLOWED_VALUES</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {subChoice.subChoiceValidationType === 'ASC_ALLOWED_VALUES' && (
              <>
                {renderFormLabel('Allowed Values (JSON)')}
                <TextField
                  value={subChoice.allowedValues ? JSON.stringify(subChoice.allowedValues, null, 2) : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) {
                      handleSubChoiceChange(index, 'allowedValues', undefined);
                      setJsonErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[index];
                        return newErrors;
                      });
                      return;
                    }
                    try {
                      const parsed = JSON.parse(value);
                      handleSubChoiceChange(index, 'allowedValues', parsed);
                      setJsonErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[index];
                        return newErrors;
                      });
                    } catch (err: any) {
                      setJsonErrors(prev => ({
                        ...prev,
                        [index]: err.message || 'Invalid JSON format'
                      }));
                    }
                  }}
                  fullWidth
                  multiline
                  rows={10}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: theme.palette.background.paper,
                    },
                    ...(getModifiedChoiceFieldStyle && isSubChoiceFieldModified
                      ? getModifiedChoiceFieldStyle(isSubChoiceFieldModified(parentChoiceIndex, index, 'allowedValues'))
                      : {}),
                  }}
                  disabled={isReadOnly}
                  error={!!jsonErrors[index]}
                  helperText={jsonErrors[index] || 'Enter valid JSON format'}
                />
              </>
            )}
          </Box>
        </Box>
      ))}
      {!isReadOnly && (
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddSubChoice}
          variant="outlined"
          size="small"
        >
          Add Sub Choice
        </Button>
      )}
    </Box>
  );
};

